<script setup>
import { nextTick, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import InlineInput from 'dashboard/components-next/inline-input/InlineInput.vue';

const props = defineProps({
  ticket: { type: Object, required: true },
  accountLabels: { type: Array, default: () => [] },
});

const emit = defineEmits(['click', 'update-title']);

const { t } = useI18n();

// Misma iconografía que las conversaciones de Chatwoot.
const PRIORITY_ICON = {
  urgent: 'i-woot-priority-urgent',
  high: 'i-woot-priority-high',
  medium: 'i-woot-priority-medium',
  low: 'i-woot-priority-low',
};

const isEditing = ref(false);
const draftTitle = ref('');
const titleInput = ref(null);

const labelColor = title =>
  props.accountLabels.find(l => l.title === title)?.color || '#94a3b8';

const startEditing = () => {
  draftTitle.value = props.ticket.title;
  isEditing.value = true;
  nextTick(() => titleInput.value?.focus?.());
};
const saveTitle = () => {
  if (!isEditing.value) return;
  isEditing.value = false;
  const title = draftTitle.value.trim();
  if (title && title !== props.ticket.title) {
    emit('update-title', { id: props.ticket.id, title });
  }
};
const cancelEditing = () => {
  isEditing.value = false;
};
</script>

<template>
  <div
    class="flex flex-col gap-1.5 p-2.5 rounded-lg cursor-pointer group bg-n-solid-1 outline outline-1 -outline-offset-1 outline-n-weak hover:outline-n-slate-6 hover:shadow-sm transition-all"
    @click="$emit('click', ticket)"
  >
    <!-- Labels con su color real (mismo chip que las conversaciones) -->
    <div
      v-if="ticket.labels && ticket.labels.length"
      class="flex flex-wrap gap-1"
    >
      <woot-label
        v-for="label in ticket.labels"
        :key="label"
        :title="label"
        :color="labelColor(label)"
        small
        variant="smooth"
      />
    </div>

    <!-- Título editable inline -->
    <div class="flex items-start gap-1.5">
      <InlineInput
        v-if="isEditing"
        ref="titleInput"
        v-model="draftTitle"
        class="flex-1"
        focus-on-mount
        @click.stop
        @enter-press="saveTitle"
        @escape-press="cancelEditing"
        @blur="saveTitle"
      />
      <template v-else>
        <p class="flex-1 text-sm font-medium leading-snug text-n-slate-12 line-clamp-3">
          {{ ticket.title }}
        </p>
        <button
          type="button"
          class="opacity-0 shrink-0 group-hover:opacity-100 text-n-slate-10 hover:text-n-slate-12 i-lucide-pencil size-3.5"
          :title="t('TICKETS.CARD.EDIT_TITLE')"
          @click.stop="startEditing"
        />
      </template>
    </div>

    <!-- Pie: número, prioridad (iconografía de conversaciones), asignado -->
    <div class="flex items-center justify-between gap-2 mt-0.5">
      <div class="flex items-center gap-2 min-w-0 text-n-slate-10">
        <span class="flex items-center gap-1">
          <Icon icon="i-lucide-ticket" class="size-3.5 shrink-0" />
          <span class="text-xs tabular-nums">#{{ ticket.display_id }}</span>
        </span>
        <Icon
          v-if="ticket.priority"
          v-tooltip.top="t(`TICKETS.PRIORITY.${ticket.priority.toUpperCase()}`)"
          :icon="PRIORITY_ICON[ticket.priority]"
          class="size-[18px] shrink-0"
        />
        <span
          v-if="ticket.conversation_id"
          v-tooltip.top="t('TICKETS.CARD.LINKED_CONVERSATION')"
          class="flex items-center gap-0.5 text-xs"
        >
          <Icon icon="i-lucide-message-circle" class="size-3.5" />
          {{ ticket.conversation_id }}
        </span>
      </div>
      <Avatar
        v-if="ticket.assignee"
        v-tooltip.top="ticket.assignee.name"
        :name="ticket.assignee.name"
        :src="ticket.assignee.avatar_url"
        :size="22"
        rounded-full
      />
      <div
        v-else
        v-tooltip.top="t('TICKETS.LIST.UNASSIGNED')"
        class="flex items-center justify-center rounded-full size-[22px] outline-dashed outline-1 outline-n-weak"
      >
        <Icon icon="i-lucide-user" class="size-3 text-n-slate-10" />
      </div>
    </div>
  </div>
</template>
